import { useState } from "react";

import { Modal } from "components/Modal/Modal";
import { PrimaryButton } from "components/Button/PrimaryButton";
import { Risk } from "components/Risk/types";

interface ImportRiskAssessmentModalProps {
  show: boolean;
  onClose: () => void;
  onImport: (risks: Risk[]) => void;
  isLoading?: boolean;
}

export const ImportRiskAssessmentModal = ({
  show,
  onClose,
  onImport,
  isLoading,
}: ImportRiskAssessmentModalProps) => {
  const [json, setJson] = useState("");
  const [error, setError] = useState<string | null>(null);

  const handleClose = () => {
    setJson("");
    setError(null);
    onClose();
  };

  const handleImport = () => {
    setError(null);
    let parsed: unknown;
    try {
      parsed = JSON.parse(json);
    } catch {
      setError("Invalid JSON — please check your input.");
      return;
    }
    if (!Array.isArray(parsed)) {
      setError("Expected a JSON array of risks.");
      return;
    }
    onImport(parsed as Risk[]);
    setJson("");
  };

  const content = (
    <div className="flex flex-col h-full">
      <h3 className="text-lg font-medium text-gray-900 mb-4">
        Import Risk Assessment
      </h3>
      <p className="text-sm text-gray-500 mb-3">
        Paste a JSON array of risks. This will replace the entire risk
        assessment for this project.
      </p>
      <textarea
        className="flex-1 w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 font-mono text-xs focus:outline-none focus:ring-cyan-500 focus:border-cyan-500"
        rows={16}
        placeholder='[{ "id": "...", "description": "...", ... }]'
        value={json}
        onChange={(e) => {
          setJson(e.target.value);
          setError(null);
        }}
      />
      {error && (
        <p className="mt-2 text-sm text-red-600">{error}</p>
      )}
      <div className="mt-4 flex justify-end gap-3">
        <button
          type="button"
          onClick={handleClose}
          className="text-sm text-gray-500 hover:text-gray-700 px-4 py-2"
        >
          Cancel
        </button>
        <PrimaryButton onClick={handleImport} disabled={isLoading || !json.trim()}>
          {isLoading ? "Importing…" : "Import"}
        </PrimaryButton>
      </div>
    </div>
  );

  return <Modal show={show} onClose={handleClose} content={content} width="1/2" />;
};
